// ScheduleModal.jsx - handles scheduling emails for later
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
} from "components/mui";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { convertUserTimeToServerTime, convertServerTimeToUserTime } from "libs/utils/timezone";

function ScheduleModal({
  open,
  onClose,
  onScheduleSet,
  onUndoSchedule,
  initialDate = null, // NEW: Allow passing initial date for edit mode
}) {
  const [scheduleDate, setScheduleDate] = useState(dayjs().add(10, "minute"));
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (open) {
      // Use initialDate if provided (edit mode), otherwise default to 10 minutes from now
      if (initialDate) {
        const userLocalDate = convertServerTimeToUserTime(initialDate);
        setScheduleDate(dayjs(userLocalDate));
      } else {
        setScheduleDate(dayjs().add(10, "minute"));
      }
      setErrorMessage("");
    }
  }, [open, initialDate]);

  const handleSchedule = () => {
    const serverTimeString = convertUserTimeToServerTime(scheduleDate.toDate());

    if (onScheduleSet) {
      onScheduleSet(serverTimeString);
    }
    onClose();
  };

  // Validate schedule date is at least 1 minute in the future
  const isValidScheduleDate = scheduleDate && scheduleDate.isAfter(dayjs().add(1, 'minute'));

  const handleUndoSchedule = () => {
    if (onUndoSchedule) {
      onUndoSchedule();
    }
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          Schedule Email
        </DialogTitle>
        <DialogContent
          sx={{
            minWidth: 400,
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Select date and time"
              value={scheduleDate}
              onChange={setScheduleDate}
              minDateTime={dayjs().add(1, 'minute')}
              ampm={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="medium"
                  sx={{ mt: 1 }}
                  helperText="Your email will be sent at the selected time"
                />
              )}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button color="error" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleUndoSchedule}
            variant="outlined"
            color="warning"
            sx={{ minWidth: 140 }}
          >
            Undo Schedule
          </Button>
          <Button
            onClick={handleSchedule}
            variant="contained"
            color="primary"
            disabled={!isValidScheduleDate}
            sx={{ minWidth: 140, fontWeight: 600 }}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ScheduleModal;
